import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Card, SectionLabel, StatusBadge, TICKET_STATUS_TONE, TICKET_STATUS_LABEL } from "../../components/customer-app/ui";
import { ChevronLeft, CheckCircle2, Circle, Loader2, PartyPopper, Sparkles, Hash, Calendar, Ticket, FileCheck2, ArrowRight } from "lucide-react";

function ActivityIcon({ status }) {
  if (status === "completed") return <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />;
  if (status === "in_process") return <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />;
  return <Circle className="w-4 h-4 text-slate-300" strokeWidth={2} />;
}

export default function CustomerTrackProgress() {
  const { oid } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customer-app/opportunities/${oid}/progress`);
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, [oid]);

  if (loading) {
    return <div className="pt-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0D4721]" /></div>;
  }
  if (!data) return null;

  const installDone = ["installation_completed", "closed"].includes(data.installation?.installation_status);

  return (
    <div className="pb-6" data-testid="customer-track-progress">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C]">
        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">{data.code}</div>
        <div className="text-lg font-extrabold mt-1">{data.name}</div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Overall Progress</div>
            <div className="text-4xl font-black mt-0.5" data-testid="track-overall-pct">{data.overall_pct}<span className="text-lg text-white/60">%</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Phase</div>
            <div className="text-lg font-bold text-amber-300">{data.current_phase} / 5</div>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
          <div className="h-full rounded-full bg-amber-400" style={{ width: `${data.overall_pct}%` }} />
        </div>
      </div>

      {/* Quotation awaiting decision — standout CTA, same as the Home alert */}
      {data.quotation_pending && (
        <Link to={`/app/opportunities/${oid}/quotation`} data-testid="track-quotation-cta"
          className="mt-4 block rounded-2xl p-4 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-[0_4px_20px_-2px_rgba(217,119,6,0.35)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
              <FileCheck2 className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-extrabold text-white">Quotation ready — review now</div>
              <div className="text-[11px] text-white/85 mt-0.5">Materials, pricing & accept/decline</div>
            </div>
            <ArrowRight className="w-4.5 h-4.5 text-white flex-shrink-0" />
          </div>
        </Link>
      )}
      {!data.quotation_pending && data.quotation_status && (
        <Link to={`/app/opportunities/${oid}/quotation`} data-testid="track-quotation-link"
          className="mt-4 flex items-center justify-between border-2 border-slate-200 rounded-xl p-3.5 hover:border-[#0D4721] transition-colors">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><FileCheck2 className="w-4 h-4 text-slate-400" /> View Quotation</div>
          <ArrowRight className="w-4 h-4 text-slate-300" />
        </Link>
      )}

      {/* Installation completion — highlighted distinctly, with full reference details */}
      {installDone && (
        <Card className="mt-4 p-4 bg-gradient-to-br from-emerald-50 to-amber-50 border-emerald-300" data-testid="track-install-complete-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <PartyPopper className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-extrabold text-emerald-800">Installation Completed!</div>
              {data.installation.service_incharge?.name && (
                <div className="text-xs text-emerald-700 mt-0.5">Handled by {data.installation.service_incharge.name}</div>
              )}
            </div>
          </div>

          <div className="mt-3.5 pt-3.5 border-t border-emerald-200/70 space-y-2.5">
            {data.installation.ticket_number && (
              <div className="flex items-center justify-between" data-testid="track-ticket-number-row">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Ticket className="w-3.5 h-3.5" /> Ticket Number</div>
                <span className="text-xs font-mono font-bold text-emerald-900">{data.installation.ticket_number}</span>
              </div>
            )}
            {data.installation.ticket_status && (
              <div className="flex items-center justify-between" data-testid="track-ticket-status-row">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Sparkles className="w-3.5 h-3.5" /> Ticket Status</div>
                <StatusBadge tone={TICKET_STATUS_TONE[data.installation.ticket_status]}>{TICKET_STATUS_LABEL[data.installation.ticket_status]}</StatusBadge>
              </div>
            )}
            {data.installation.ib_number && (
              <div className="flex items-center justify-between" data-testid="track-ib-number-row">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Hash className="w-3.5 h-3.5" /> Installation ID</div>
                <Link to={`/app/installations?open=${data.installation.ib_id}`} data-testid="track-ib-number-link"
                  className="text-xs font-mono font-bold text-emerald-900 underline">
                  {data.installation.ib_number}
                </Link>
              </div>
            )}
            {data.installation.commissioning_date && (
              <div className="flex items-center justify-between" data-testid="track-installation-date-row">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Calendar className="w-3.5 h-3.5" /> Date of Installation</div>
                <span className="text-xs font-bold text-emerald-900">{new Date(data.installation.commissioning_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
            )}
          </div>
        </Card>
      )}
      {!installDone && data.installation?.installation_status !== "not_logged" && (
        <Card className="mt-4 p-3.5" data-testid="track-install-status-banner">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <div className="text-sm font-semibold text-slate-700">{data.installation.installation_status_label}</div>
          </div>
          {data.installation.ticket_number && (
            <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100">
              <div className="flex items-center gap-1.5 text-xs text-slate-500"><Ticket className="w-3.5 h-3.5" /> Ticket Number</div>
              <span className="text-xs font-mono font-bold text-slate-700">{data.installation.ticket_number}</span>
            </div>
          )}
          {data.installation.ticket_status && (
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">Ticket Status</div>
              <StatusBadge tone={TICKET_STATUS_TONE[data.installation.ticket_status]}>{TICKET_STATUS_LABEL[data.installation.ticket_status]}</StatusBadge>
            </div>
          )}
        </Card>
      )}

      <div className="mt-6">
        <SectionLabel className="mb-3">Five Phases — Your Project Journey</SectionLabel>
        <div className="space-y-5">
          {data.phases.map((p, i) => {
            const isCurrent = p.phase === data.current_phase;
            const allDone = p.total > 0 && p.done === p.total;
            return (
              <div key={p.phase} className={"relative pl-7 " + (i < data.phases.length - 1 ? "pb-5 border-l-2 ml-2.5" : "ml-2.5") + " " + (allDone ? "border-emerald-400" : "border-slate-200")} data-testid={`track-phase-${p.phase}`}>
                <div className={"absolute -left-[11px] top-0 w-5.5 h-5.5 rounded-full flex items-center justify-center text-[10px] font-black " + (allDone ? "bg-emerald-600 text-white" : isCurrent ? "bg-amber-400 text-[#0D4721] ring-4 ring-amber-200" : "bg-white border-2 border-slate-200 text-slate-400")}>
                  {allDone ? <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> : p.phase}
                </div>
                <div className="text-sm font-bold text-slate-800">{p.phase_name}</div>
                <div className="text-[11px] font-mono text-slate-400 mt-0.5">{p.done}/{p.total} done{isCurrent && !allDone ? " · current" : ""}</div>
                <ul className="mt-2 space-y-1.5">
                  {p.activities.map((a, ai) => (
                    <li key={ai} className="flex items-start gap-2 text-xs text-slate-600">
                      <ActivityIcon status={a.status} /> <span className="mt-0.5">{a.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
